import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@jobcost-monorepo/i18n';
import { cn, InvoicingFeatureName } from '@jobcost-monorepo/utils';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../..';
import { IconNames } from '../../atoms/Icons';
import SublistCard, { SubListProps } from './subListCard';

export interface CardProps {
  iconName: IconNames;
  label: string;
  navigateTo?: string;
  subList?: SubListProps[];
  adminOnly?: boolean;
  /** Voce visibile solo se la funzione è attiva per il provider di fatturazione. */
  feature?: InvoicingFeatureName;
  setSidebarOpenMobile?: (value: boolean) => void;
  subListRef?: React.RefObject<HTMLDivElement>;
}

interface Props extends Omit<CardProps, 'navigateTo'> {
  onClick: () => void;
  isOpenSidebar: boolean;
  setIsOpenSidebar: () => void;
  isOpenSublist?: boolean;
  isSelected?: boolean;
}

const SidebarMenuItem: React.FC<Props> = ({
  iconName,
  label,
  onClick,
  subList,
  isOpenSidebar,
  setIsOpenSidebar,
  isOpenSublist,
  isSelected,
  setSidebarOpenMobile,
}) => {
  const { t } = useTranslation('sidebar');
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname.split('/')[1];
  const isSubListSelected =
    subList?.findIndex((el) => el.navigateTo === path) ?? -1;

  const subListRef = useRef<HTMLDivElement>(null);

  const handleNavigate = (item: string) => {
    setSidebarOpenMobile?.(false);
    navigate(item);
  };

  const ChevronIcon: React.FC<{ className: string }> = ({ className }) => {
    return isOpenSublist ? (
      <ChevronDownIcon className={className} />
    ) : (
      <ChevronRightIcon className={className} />
    );
  };

  useEffect(() => {
    if (isOpenSublist) {
      subListRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [isOpenSublist]);

  return (
    <div className="flex flex-col w-full space-y-1" ref={subListRef}>
      <div className="flex">
        <div className="w-1.5 bg-none" />
        <div
          className={cn(
            'flex w-full py-2 px-1 rounded-lg items-center hover:bg-violet5 dark:hover:bg-violet40 cursor-pointer',
            isOpenSidebar ? 'justify-start' : 'justify-center',
            isSelected || isSubListSelected >= 0 ? 'text-pink' : '',
          )}
          onClick={
            !isOpenSidebar && subList
              ? () => {
                  setIsOpenSidebar();
                  onClick();
                }
              : onClick
          }
        >
          <Icon
            iconName={iconName}
            className={cn(
              isSelected || isSubListSelected >= 0
                ? 'fill-pink stroke-pink'
                : 'fill-black stroke-black dark:fill-white dark:stroke-white',
            )}
          />
          {isOpenSidebar && (
            <>
              <span
                className={cn(
                  'flex-1 flex ellipsify pl-1.5 ',
                  isSelected || isSubListSelected >= 0
                    ? 'text-pink'
                    : 'text-black dark:text-white',
                )}
              >
                {t(label)}
              </span>
              {subList && (
                <ChevronIcon
                  className={cn(
                    'mr-4 w-5 h-5',
                    isSelected || isSubListSelected >= 0
                      ? 'text-pink'
                      : 'text-black dark:text-white',
                  )}
                />
              )}
            </>
          )}
        </div>
        <div
          className={cn(
            'w-1.5 rounded-tl-lg rounded-bl-lg',
            isSelected || isSubListSelected >= 0 ? 'bg-pink' : 'bg-none',
          )}
        />
      </div>

      {isOpenSublist && (
        <div className="ml-3 pl-3 border-l-2 border-violet5">
          {subList?.map((item, index) => (
            <SublistCard
              key={`${item.label}_${index}`}
              label={item.label}
              onClick={() => handleNavigate(item.navigateTo)}
              isSelected={isSubListSelected === index}
              adminOnly={item.adminOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SidebarMenuItem;
